import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { lastValueFrom, Subscription } from 'rxjs';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ListStateService } from '../services/list-state/list-state.service';

import { DynamicListingComponent, FilterConfig, ColumnConfig, ActionConfig, ActionEvent, FilterEvent } from '../dynamic-listing/dynamic-listing';
import { ClientListService } from '../services/client/client-list.service';
import { ClientPaymentService } from '../services/client/client-payment.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { MastersService } from 'libs/shared/src/lib/services/masters/masters.service';
import { SVG_ICONS } from '../utils/svg-icons.utils';
import { ExportApiService } from '../services/export/export-api.service';
import { ExportHelper } from '../utils/export-helper.utils';

interface ClientRefundRow {
  remarks?: string;
  id: string;
  transaction_no: string;
  refund_date: string;
  client_id: string;
  client_name: string;
  refund_amount: number;
  refund_method: string;
  reason: string;
  status: string;
  approval_status?: string;
  line_of_business?: string;
  is_cancelled?: boolean;
  _original?: Record<string, unknown>;
}

@Component({
  selector: 'lib-client-refund-list',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent, DynamicDialogModule],
  providers: [DialogService],
  templateUrl: './client-refund-list.html',
  styleUrls: ['./client-refund-list.css'],
})
export class ClientRefundList implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly listStateService = inject(ListStateService);
  private pageSubscription?: Subscription;

  isLoading = false;
  isLoadingClients = false;
  exportFilename = `client_refunds_${new Date().toISOString().slice(0, 10)}`;

  tableData: ClientRefundRow[] = [];
  totalAmount = 0;

  // Filters
  refundDateFrom: Date | null = null;
  refundDateTo: Date | null = null;
  lineOfBusiness : any = '';

  client = '';
  status = '';

  // Filter configurations
  filterConfigs: FilterConfig[] = [];

  // Pagination
  pageIndex = 1;
  rowsPerPage = 10;
  totalCount = 0;
  totalPages = 0;
  columnPreferenceId?: string;

  // Filter options
  clientOptions: { label: string; value: string }[] = [];
  clientSuggestions: any[] = [];
  private clientLabelMap = new Map<string, string>();
  statusOptions: any[] = [];
  lineOfBusinessOptions: any[] = [];
  private lobMap: Record<string, string> = {};

  // Column configuration for dynamic listing
  columnConfigs: ColumnConfig[] = [
    { key: 'transaction_no', header: 'Transaction No.' },
    { key: 'refund_date', header: 'Refund Date', type: 'date' },
    { key: 'client_name', header: 'Client Name', ellipsis: true },
    { key: 'refund_amount', header: 'Refund Amount', type: 'currency' },
    { key: 'refund_method', header: 'Refund Method' },
    { key: 'reason', header: 'Reason', ellipsis: true },
    { key: 'status', header: 'Status', type: 'status' },
    { key: 'approval_status', header: 'Approval Status', type: 'status' },
    { key: 'line_of_business', header: 'Line Of Business'}
  ];

  actionConfigs: ActionConfig[] = [
    {
      key: 'view',
      label: 'View',
      tooltip: 'View refund',
      severity: 'secondary',
      svgIcon: SVG_ICONS.VIEW,
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit refund',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT,
      disabled: (row: any) => {
        const approvalStatus = row?._original?.approval_status || row?.approval_status || '';
        const isCancelled = row?.is_cancelled || row?._original?.is_cancelled || row?.status === 'Cancelled' || row?._original?.status === 3;
        return isCancelled || approvalStatus.toLowerCase().replace(/\s/g, '_') === 'approved';
      },
      disabledTooltip: (row: any) => {
        const isCancelled = row?.is_cancelled || row?._original?.is_cancelled || row?.status === 'Cancelled' || row?._original?.status === 3;
        if (isCancelled) return 'Cannot edit cancelled refund';
        return 'Cannot edit refund with approved status';
      }
    },
    {
      key: 'delete',
      label: 'Delete',
      tooltip: 'Delete refund',
      severity: 'secondary',
      svgIcon: SVG_ICONS.DELETE,
      disabled: (row: any) => {
        const approvalStatus = row?._original?.approval_status || row?.approval_status || '';
        const isCancelled = row?.is_cancelled || row?._original?.is_cancelled || row?.status === 'Cancelled' || row?._original?.status === 3;
        return isCancelled || approvalStatus.toLowerCase().replace(/\s/g, '_') === 'approved';
      },
      disabledTooltip: (row: any) => {
        const isCancelled = row?.is_cancelled || row?._original?.is_cancelled || row?.status === 'Cancelled' || row?._original?.status === 3;
        if (isCancelled) return 'Cannot delete cancelled refund';
        return 'Cannot delete refund with approved status';
      }
    },
    {
      key: 'cancel',
      label: 'Cancel',
      tooltip: 'Cancel',
      severity: 'secondary',
      svgIcon: SVG_ICONS.CROSS_ICON,
      disabled: (row: any) => {
        const approvalStatus = row?._original?.approval_status || row?.approval_status || '';
        const isCancelled = row?.is_cancelled || row?._original?.is_cancelled || row?.status === 'Cancelled' || row?._original?.status === 3;
        return isCancelled || approvalStatus.toLowerCase().replace(/\s/g, '_') === 'approved';
      },
      disabledTooltip: (row: any) => {
        const isCancelled = row?.is_cancelled || row?._original?.is_cancelled || row?.status === 'Cancelled' || row?._original?.status === 3;
        if (isCancelled) return 'Refund is already cancelled';
        return 'Cannot cancel refund with approved status';
      }
    },
  ];

  headerActionConfigs: ActionConfig[] = [];

  constructor(
    private router: Router,
    private clientService: ClientListService,
    private clientPaymentService: ClientPaymentService,
    private mastersService: MastersService,
    private toasterService: ToasterService,
    private dialogService: DialogService,
    private exportApiService: ExportApiService
  ) {}

  ngOnInit() {
    this.initializeFilterConfigs();

    // Subscribe to URL page changes
    this.pageSubscription = this.listStateService.getPageFromRoute(this.route).subscribe(page => {
      this.loadClientRefunds(page);
    });
  }

  ngOnDestroy() {
    this.pageSubscription?.unsubscribe();
  }

  async initializeFilterConfigs(): Promise<void> {
    // load options from masters
    await this.loadLineOfBusiness();


    this.filterConfigs = [
      {
        key: 'refundDateFrom',
        type: 'date',
        placeholder: 'Posting Date From',
        value: this.refundDateFrom,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'refundDateTo',
        type: 'date',
        placeholder: 'Posting Date To',
        value: this.refundDateTo,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'client',
        type: 'autocomplete',
        placeholder: 'Client',
        suggestions: this.clientSuggestions,
        optionLabel: 'label',
        optionValue: 'value',
        value: this.client,
        loading: this.isLoadingClients,
        showInAccordion: true,
        showInDialog: false,
        completeMethod: this.searchClients.bind(this)
      },
      {
        key: 'status',
        type: 'select',
        placeholder: 'Status',
        options: this.statusOptions,
        optionLabel: 'label',
        optionValue: 'value',
        value: this.status,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'lineOfBusiness',
        type: 'select',
        placeholder: 'Line Of Business',
        options: this.lineOfBusinessOptions,
        optionLabel: 'label',
        optionValue: 'value',
        value: this.lineOfBusiness, // Set to null so dropdown shows no selection, but partyType is still 'all' internally
        showInAccordion: true,
        showInDialog: false
      },
    ];
  }

  async loadLineOfBusiness() {
    try {
      const response = await lastValueFrom(
        this.mastersService.getMasters(['line-of-businesses'])
      ) as any;

      const mastersData = response?.data || response || {};
      const lobs = Array.isArray(mastersData['line-of-businesses'])
        ? mastersData['line-of-businesses']
        : [];

      const mapped = lobs.map((lob: any) => ({
        value: lob.id || lob.code,
        label: (lob.name || lob.line_of_business_name || `LOB ${lob.id}`)
          .toString()
          .toUpperCase()
      }));

      this.lineOfBusinessOptions.splice(
        0,
        this.lineOfBusinessOptions.length,
        ...mapped
      );

      this.lobMap = {};
      for (const rec of mapped) {
        this.lobMap[rec.value] = rec.label;
      }

    } catch (error) {
      console.error('Error loading line of business:', error);
      this.lineOfBusinessOptions.splice(0, this.lineOfBusinessOptions.length);
    }
  }

  async searchClients(event: { query: string; filter: any }) {
    if (this.isLoadingClients) return;
    const query = (event.query || '').trim();

    try {
      this.isLoadingClients = true;

      const filters: any = {
        type: 'all',
        limit: 50,
        offset: 0,
        include_cancelled: 'false'
      };

      // Add search query if provided
      if (query) {
        filters.query = query;
      }

      const response = await lastValueFrom(this.clientService.getClients(filters));

      if (response?.data) {
        const clients = response.data.clients || [];
        this.clientSuggestions = clients.map((c: any) => {
          const id = String(c.id ?? c._id ?? c.uuid ?? '');
          const label = `${c.client_code} - ${c.client_name}` || c.client_name || c.name || c.client_code || 'Unknown';

          // Update clientLabelMap for display purposes
          if (id) {
            this.clientLabelMap.set(id, label);
          }

          return {
            label,
            value: id,
            ...c
          };
        }).filter((option: any) => option.value);

        // Update the filter's suggestions
        if (event.filter) {
          event.filter.suggestions = this.clientSuggestions;
        }
      } else {
        this.clientSuggestions = [];
      }
    } catch (error) {
      console.error('Error searching clients:', error);
      this.clientSuggestions = [];
    } finally {
      this.isLoadingClients = false;
    }
  }

  async loadClientRefunds(page = 1): Promise<void> {
    try {
      this.isLoading = true;
      this.pageIndex = page;

      const filters: any = {
        limit: this.rowsPerPage,
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        include_cancelled: 'true',
        is_refund: true,
        get_references: 'true',
      };

      if (this.refundDateFrom) {
        filters.refund_date_from = this.formatDateForApi(this.refundDateFrom);
      }

      if (this.refundDateTo) {
        filters.refund_date_to = this.formatDateForApi(this.refundDateTo);
      }

      if (this.lineOfBusiness && String(this.lineOfBusiness).trim() !== '') {
        filters.line_of_business = this.lineOfBusiness;
      }

      if (this.client) {
        // Ensure we use the ID value, not the object
        let clientId: string = '';
        if (typeof this.client === 'object' && this.client !== null) {
          clientId = String((this.client as any).value || (this.client as any).id || '');
        } else {
          clientId = String(this.client);
        }

        if (clientId) {
          filters.client_id = clientId;
        }
      }

      if (this.status) {
        filters.status = this.status;
      }

      // Use dedicated refund API endpoint
      const response = await lastValueFrom(this.clientPaymentService.getClientRefunds(filters));

      // Extract column preference from response
      if (response?.data?.column_preference) {
        const columnPreference = response.data.column_preference;
        this.columnPreferenceId = columnPreference.id ? String(columnPreference.id) : undefined;

        // Update columnConfigs with columns_list from preference
        if (columnPreference.columns_list && Array.isArray(columnPreference.columns_list)) {
          this.columnConfigs = columnPreference.columns_list.map((col: any) => ({
            key: col.key,
            header: col.header || col.label,
            type: col.type,
            ellipsis: col.ellipsis,
            align: col.align,
            is_selected: col.is_selected !== false,
          }));
        }

      }

      const rawData: any[] = this.extractRefundArray(response);
      this.tableData = rawData.map(item => this.mapRefundRow(item));

      this.totalCount = response?.data?.total;
      this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.rowsPerPage));
      this.totalAmount = this.tableData.reduce((sum, refund) => sum + (refund.refund_amount || 0), 0);
    } catch (error) {
      console.error('Error loading client refunds:', error);
      this.toasterService.error('Error', 'Failed to load client refunds');
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.totalAmount = 0;
    } finally {
      this.isLoading = false;
    }
  }

  private extractRefundArray(response: any): any[] {
    if (!response?.data) {
      return [];
    }

    // Handle refund API response structure
    if (Array.isArray(response.data.client_refund)) {
      return response.data.client_refund;
    }


    return [];
  }

  private mapRefundRow(item: any): ClientRefundRow {
    // Handle refund API response structure - refund data might be directly in item or nested
    const refund = item?.client_refund || item?.refund || item?.client_payment || item || {};

    const rawApprovalStatus = refund.approval_status || item?.approval_status || 'not_required';

    return {
      id: refund.id || item?.id || '',
      transaction_no: refund.transaction_no || item?.transaction_no || '-',
      refund_date: refund.refund_date || refund.payment_date || item?.refund_date || item?.payment_date || '',
      client_id: refund.client_id || item?.client_id || '-',
      client_name:
        this.clientLabelMap.get(String(refund.client_id ?? item?.client_id ?? '')) ||
        refund.client_name ||
        refund.client?.name ||
        item?.client_name ||
        item?.client?.name ||
        '-',
      refund_amount: Number(
        refund.refund_amount ??
        refund.total_refund_amount ??
        refund.total_receivable_amount ??
        refund.advance_amount ??
        item?.refund_amount ??
        0
      ),
      refund_method: refund.refund_method || refund.payment_method || item?.refund_method || item?.payment_method || 'Refund',
      reason: refund.remarks || refund.memo || refund.notes || item?.reason || item?.memo || item?.notes || 'No reason provided',
      status: this.getStatusString(refund.status ?? item?.status),
      approval_status: this.formatApprovalStatus(rawApprovalStatus),
      line_of_business: this.lobMap[refund.line_of_business_id || refund.line_of_business] || '-',
      is_cancelled: Boolean(refund.is_cancelled || item?.is_cancelled) || (refund.status === 'Cancelled' || refund.status === 3 || item?.status === 3),
      _original: refund,
    };
  }

  onFilterEvent(event: FilterEvent): void {
    const data = event.data ?? {};
    this.refundDateFrom = data.refundDateFrom ?? null;
    this.refundDateTo = data.refundDateTo ?? null;
    this.lineOfBusiness = event.data?.lineOfBusiness ?? '';


    // Extract client ID from object if it's an object, otherwise use the value directly
    const clientValue = data.client;
    if (clientValue && typeof clientValue === 'object') {
      this.client = clientValue.value || clientValue.id || '';
    } else {
      this.client = clientValue || '';
    }

    this.status = data.status ?? '';

    this.initializeFilterConfigs();
    this.listStateService.clearPage();
    this.loadClientRefunds(1);
  }

  onColumnsSelected(_: string[]): void {
    // Placeholder for future column preference persistence
  }

  onColumnPreferenceSaved(event: { id: string; columns: ColumnConfig[] }) {
    this.columnPreferenceId = event.id ? String(event.id) : undefined;
    this.columnConfigs = event.columns;
  }

  onActionClicked(event: ActionEvent): void {
    if (event.row === null) {
      // Header action
      switch (event.action) {
        case 'createRefund':
          this.createRefund();
          break;
        case 'bulkUpload':
          this.bulkUpload();
          break;
      }
      return;
    }

    const refundId = event.row?.id;
    if (!refundId) return;

    switch (event.action) {
      case 'view':
        this.view(refundId);
        break;
      case 'edit':
        this.edit(refundId);
        break;
      case 'delete':
        this.delete(refundId);
        break;
      case 'cancel':
        this.cancel(refundId);
        break;
    }
  }

  onPageChanged(page: number): void {
    this.listStateService.setPage(page);
  }

  exportCsv(): void {
    this.openExportDialog('csv');
  }

  exportXlsx(): void {
    this.openExportDialog('excel');
  }

  exportPdf(): void {
    window.print();
  }

  private openExportDialog(format: 'excel' | 'csv'): void {
    const filters = this.getCurrentFilters();

    ExportHelper.openExportDialog({
      module: 'client_refund',
      columns: this.exportColumns,
      filters,
      hasActiveFilters: ExportHelper.hasActiveFilters(filters),
      defaultFormat: format,
      title: 'Export Client Refunds',
    }, this.dialogService, this.exportApiService, this.toasterService);
  }

  private readonly exportColumns = [
    { key: 'transaction_no', header: 'Transaction No.' },
    { key: 'refund_date', header: 'Refund Date' },
    { key: 'posting_date', header: 'Posting Date' },
    { key: 'client_id', header: 'Client ID' },
    { key: 'client_code', header: 'Client Code' },
    { key: 'client_name', header: 'Client Name' },
    { key: 'refund_amount', header: 'Refund Amount' },
    { key: 'refund_method', header: 'Refund Method' },
    { key: 'reason', header: 'Reason' },
    { key: 'payment_reference_no', header: 'Payment Reference No' },
    { key: 'status', header: 'Status' },
    { key: 'approval_status', header: 'Approval Status' },
    { key: 'accounting_status', header: 'Accounting Status' },
    { key: 'line_of_business', header: 'Line Of Business' },
  ];

  private getCurrentFilters(): Record<string, any> {
    return {
      client_id: this.client || undefined,
      line_of_business:
        this.lineOfBusiness && String(this.lineOfBusiness).trim()
          ? this.lineOfBusiness
          : undefined,
      approval_status: this.status || undefined,
      refund_date_from: this.refundDateFrom
        ? moment(this.refundDateFrom).format('YYYY-MM-DD')
        : undefined,
      refund_date_to: this.refundDateTo
        ? moment(this.refundDateTo).format('YYYY-MM-DD')
        : undefined,
    };
  }

 view(refundId: string): void {
  if (!refundId) return;

  this.mastersService.openInNewTab(
    ['/clients/refund-payment'],
    { clientRefundId: refundId, mode: 'view' }
  );
}

edit(refundId: string): void {
  if (!refundId) return;

  this.mastersService.openInNewTab(
    ['/clients/refund-payment'],
    { clientRefundId: refundId, mode: 'edit' }
  );
}

  delete(refundId: string): void {
    if (!confirm('Are you sure you want to delete this refund?')) {
      return;
    }

    // Use dedicated refund API endpoint
    this.clientPaymentService.deleteClientRefund(refundId).subscribe({
      next: () => {
        this.toasterService.success('Success', 'Refund deleted successfully');
        this.loadClientRefunds();
      },
      error: (error) => {
        const message = error?.error?.message || error?.message || 'Failed to delete refund';
        this.toasterService.error('Error', message);
      },
    });
  }

  cancel(refundId: string): void {
    if (!confirm('Are you sure you want to cancel this refund?')) {
      return;
    }

    this.isLoading = true; // Show loader immediately
    this.clientPaymentService.cancelClientRefund(refundId).subscribe({
      next: () => {
        this.toasterService.success('Success', 'Refund cancelled successfully');
        this.loadClientRefunds();
      },
      error: (error) => {
        this.isLoading = false;
        const message = error?.error?.message || error?.message || 'Failed to cancel refund';
        this.toasterService.error('Error', message);
      },
    });
  }

  createRefund(): void {
    this.router.navigate(['/clients/refund-payment']);
  }

  bulkUpload(): void {
    // TODO: Implement bulk upload functionality
    this.toasterService.info('Info', 'Bulk upload functionality coming soon.');
  }

  private formatDateForApi(date: Date): string {
    return moment(date).format('YYYY-MM-DD');
  }

  private getStatusString(status: number | string | undefined): string {
    if (status === undefined || status === null) return 'Pending';
    if (typeof status === 'string') {
      // Capitalize first letter for consistency
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }

    const statusMap: Record<number, string> = {
      0: 'Pending',
      1: 'Processed',
      2: 'Refunded',
      3: 'Cancelled',
    };

    return statusMap[status] || 'Pending';
  }

  private formatApprovalStatus(status: string | undefined): string {
    if (!status) return 'Not required';
    const formatted = status.replace(/_/g, ' ');
    return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
  }
}
